import type { ToastProps } from '@src/hooks/Toast/useToasts';
import type { MouseEvent } from 'react';
import styled from 'styled-components';
import { modeColor } from '../../styles';
import { Box, type BoxProps } from '../Core';
import { NotificationInternalToast } from '../Notification';

/** Internal (classic) popup toasts (unrelated to server-driven to AppInboxToasts, which get driven by the the appNotification service) */
export function ToastInternal({
  onRemove,
  boxProps,
  ...toast
}: ToastProps & { onRemove: () => void; boxProps: BoxProps }) {
  return (
    <ToastInternalWrapper
      {...boxProps}
      data-testid="toast-wrapper"
      onClickCapture={dismissOnActionButtonClick(onRemove)}
    >
      <NotificationInternalToast variant={toast.variant} dismissable={toast.dismissable} onRemove={onRemove}>
        {toast.text}
      </NotificationInternalToast>
    </ToastInternalWrapper>
  );
}

/** Buttons in toast content are actions. Clicking one dismisses the toast; the button's own handler still runs. */
function dismissOnActionButtonClick(onRemove: () => void) {
  return (event: MouseEvent) => {
    const target = event.target;
    if (target instanceof Element && target.closest('button') != null) {
      onRemove();
    }
  };
}

const ToastInternalWrapper = styled(Box)`
  box-shadow: 0 5px 40px ${({ theme }) => modeColor(theme, 'hsla(0, 0%, 0%, 0.1)', theme.colors.black.dim)};
  position: relative;
  pointer-events: auto;
  border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  overflow: hidden;

  ${NotificationInternalToast} {
    &:not(:first-child) {
      ${({ theme }) => `margin-top: ${theme.spacing4}px;`}
    }
    &:not(:last-child) {
      ${({ theme }) => `margin-bottom: ${theme.spacing4}px;`}
    }
  }

  div {
    word-break: break-word;
  }
`;
